import {
  ExtractKeywordsResponseSchema,
  MatchResponseSchema,
  type ExtractKeywordsRequest,
  type ExtractKeywordsResponse,
  type MatchRequest,
  type MatchResponse,
} from '@applicator/shared'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Match a resume against a job description using the ATS API
 * @param payload - The match request payload
 * @returns Promise resolving to the match response
 * @throws Error if the request fails or response validation fails
 */
export async function matchAts(payload: MatchRequest): Promise<MatchResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/ats/match`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}. ${errorText || ''}`
      )
    }

    const data = await response.json()

    // Validate response with Zod schema
    const validatedData = MatchResponseSchema.parse(data)
    return validatedData
  } catch (error) {
    if (error instanceof Error) {
      // Re-throw with more context if it's a validation error
      if (error.name === 'ZodError') {
        throw new Error(
          `Response validation failed: ${error.message}. The API response format may have changed.`
        )
      }
      throw error
    }
    throw new Error(`Unexpected error during ATS matching: ${String(error)}`)
  }
}

/**
 * Extract sectioned keywords from a job description via the ATS API.
 */
export async function extractJobKeywords(
  payload: ExtractKeywordsRequest,
  authToken?: string,
  debugEnabled = false,
): Promise<ExtractKeywordsResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`
  }
  if (debugEnabled) {
    headers['X-ATS-Debug'] = '1'
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/ats/extract-keywords`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Keyword extraction failed: ${response.status} ${response.statusText}. ${errorText || ''}`,
    )
  }

  const raw = await response.text()
  const data = JSON.parse(raw) as unknown
  return ExtractKeywordsResponseSchema.parse(data)
}
